/**
 * @fileoverview ESM mirror of ../tenantOrigin.js.
 *
 * See the CJS source for full documentation. Drift between the two is
 * caught by `tests/unit/esmParity.behaviour.test.js` (a differential over
 * both halves) and `tests/esm/native-import.test.mjs`.
 *
 * @module @puffin-core/cross-app-policy/tenantOrigin
 */

const ENV_PREFIX_REGEX = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/i;

function readDomainEnvVarOverride(target) {
  const envVar = target.domainEnvVar;
  if (typeof envVar !== 'string' || envVar.length === 0) return undefined;
  const override = typeof process !== 'undefined' && process.env ? process.env[envVar] : undefined;
  if (typeof override !== 'string' || override.length === 0) return undefined;
  return override;
}

function composeEnvOrProdDomain(target, envPrefix, caller) {
  if (typeof envPrefix === 'string' && envPrefix.length > 0) {
    if (!ENV_PREFIX_REGEX.test(envPrefix)) {
      throw new Error(
        `${caller}: envPrefix invalid ("${envPrefix}") — must be hostname-label-shaped (alnum + hyphens, dot-separated)`,
      );
    }
    return `${envPrefix}.${target.defaultDomain}`;
  }
  return target.defaultDomain;
}

function composeTenantOrigin({ target, subdomain, envPrefix, local, caller }) {
  const override = readDomainEnvVarOverride(target);
  if (override) {
    return `https://${subdomain}.${override}`;
  }
  if (local) {
    if (typeof target.webDevPort !== 'number' || !Number.isInteger(target.webDevPort)) {
      throw new Error(
        `${caller}: target.webDevPort is required for localhost source (target.appKey=${
          target.appKey || '?'
        })`,
      );
    }
    return `http://${subdomain}.localhost:${target.webDevPort}`;
  }
  return `https://${subdomain}.${composeEnvOrProdDomain(target, envPrefix, caller)}`;
}

function composeConsoleOrigin({ target, local }) {
  const override = readDomainEnvVarOverride(target);
  if (override) {
    return `https://${override}`;
  }
  if (local) {
    return `http://localhost:${target.webDevPort}`;
  }
  return `https://${target.defaultDomain}`;
}

const SAFE_PATH_DEFECTS = Object.freeze({
  NOT_A_PATH: 'must be a path beginning with "/" (no protocol-relative)',
  CONTROL_CHAR: 'must be a path (control chars rejected)',
  SCHEME_SEGMENT: 'path must not contain ":" (scheme-like segment rejected)',
  BACKSLASH: 'path must not contain "\\" (browsers read it as "/")',
});

function safeRelativePathDefect(value) {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) {
    return SAFE_PATH_DEFECTS.NOT_A_PATH;
  }
  for (let i = 0; i < value.length; i += 1) {
    if (value.charCodeAt(i) < 32) {
      return SAFE_PATH_DEFECTS.CONTROL_CHAR;
    }
  }
  const pathOnly = value.split(/[?#]/, 1)[0];
  if (pathOnly.includes(':')) {
    return SAFE_PATH_DEFECTS.SCHEME_SEGMENT;
  }
  if (pathOnly.includes('\\')) {
    return SAFE_PATH_DEFECTS.BACKSLASH;
  }
  return null;
}

function assertSafeRelativePath(value, subject) {
  const defect = safeRelativePathDefect(value);
  if (defect !== null) {
    throw new Error(`${subject} ${defect}`);
  }
}

export {
  composeTenantOrigin,
  composeConsoleOrigin,
  SAFE_PATH_DEFECTS,
  safeRelativePathDefect,
  assertSafeRelativePath,
};
